import React from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabase';

type ReturningUserPageProps = {
  email: string;
  onboardingStarted: boolean;
  accountExists: boolean;
  isGuide?: boolean;
};

export function ReturningUserPage({
  email,
  onboardingStarted,
  accountExists,
  isGuide = false,
}: ReturningUserPageProps) {
  const navigate = useNavigate();
  const [sending, setSending] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleSendEmail = async () => {
    setSending(true);
    setError(null);

    try {
      const supabase = getSupabaseClient();

      // Call the send-continue-onboarding edge function
      const { data, error: invokeError } = await supabase.functions.invoke(
        'send-continue-onboarding',
        {
          body: {
            seeker_email: email,
          },
        }
      );

      if (invokeError) {
        throw new Error(invokeError.message || 'Failed to send continue onboarding email');
      }

      console.log('Continue onboarding email sent:', data);
      setSent(true);
    } catch (err) {
      console.error('Failed to send email:', err);
      setError(err instanceof Error ? err.message : 'Failed to send email. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleLogin = () => {
    navigate('/login');
  };

  return (
    <div className="flex h-screen min-h-screen w-full items-stretch overflow-hidden">
      <div className="relative flex h-full w-full flex-1 flex-col overflow-hidden bg-[#FFFCEF]">
        {/* Header */}
        <header className="flex h-16 items-center border-b border-[#FFFCEF]/25 bg-[#2C2731] px-4">
          <div className="w-10" aria-hidden />
          <div className="flex flex-1 justify-center">
            <img src="/gclogo.webp" alt="Guides Collective" className="h-8 w-auto" />
          </div>
          <div className="w-10" aria-hidden />
        </header>

        {/* Main Content */}
        <main className="flex flex-1 min-h-0 flex-col items-center justify-center text-center px-6">
          <div className="animate-fade-in flex flex-col items-center gap-6 max-w-md">
            {/* Icon */}
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#304031]/10">
              <svg
                className="h-10 w-10 text-[#304031]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>

            {/* Title */}
            <h1 className="font-recoleta text-[42px] font-normal leading-[1.1] text-[#304031]">
              It looks like we've seen you before
            </h1>

            {/* Email Display */}
            {email && (
              <div className="rounded-xl border border-[#304031]/20 bg-[#FDF1C3]/70 px-4 py-3">
                <div className="text-sm text-[#214D3C]/70 mb-1">Email address</div>
                <div className="text-base font-medium text-[#304031]">{email}</div>
              </div>
            )}

            {/* Description */}
            {!email ? (
              <p className="text-[18px] leading-relaxed text-[#214D3C]">
                Something went wrong. Please try again.
              </p>
            ) : isGuide ? (
              <p className="text-[18px] leading-relaxed text-[#214D3C]">
                This email is associated with a guide account. Please use a different email to sign up as a seeker.
              </p>
            ) : accountExists ? (
              <p className="text-[18px] leading-relaxed text-[#214D3C]">
                You already have an account with us. Please log in to continue your journey.
              </p>
            ) : onboardingStarted ? (
              <p className="text-[18px] leading-relaxed text-[#214D3C]">
                You've already started your onboarding. We'll send you an email to continue where you left off.
              </p>
            ) : null}

            {/* Error Message */}
            {error && (
              <div className="w-full rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {error}
              </div>
            )}

            {/* Success Message */}
            {sent && (
              <div className="w-full rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                Email sent! Please check your inbox and follow the link to continue.
              </div>
            )}
          </div>
        </main>

        {/* Footer with CTA */}
        <footer className="sticky bottom-0 z-10 mt-auto border-t border-[#2C2731]/18 bg-gradient-to-t from-[#FFFCEF] via-[#FFFCEF] to-[#FFFCEF]/0 px-6 pb-7 pt-5 shadow-[0_-12px_26px_-24px_rgba(44,39,49,0.55)]">
          <div className="w-full max-w-md mx-auto">
            {!email ? (
              <button
                type="button"
                onClick={() => navigate('/')}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#304031] bg-[#FEF6D6] px-6 py-4 text-lg font-semibold text-[#304031] shadow-[0_16px_36px_-20px_rgba(48,64,49,0.45)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#304031] hover:text-[#FEF6D6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#304031]"
              >
                Try again
              </button>
            ) : isGuide ? (
              <button
                type="button"
                onClick={() => navigate('/')}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#304031] bg-[#FEF6D6] px-6 py-4 text-lg font-semibold text-[#304031] shadow-[0_16px_36px_-20px_rgba(48,64,49,0.45)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#304031] hover:text-[#FEF6D6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#304031]"
              >
                Use a different email
              </button>
            ) : accountExists ? (
              <button
                type="button"
                onClick={handleLogin}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#304031] bg-[#FEF6D6] px-6 py-4 text-lg font-semibold text-[#304031] shadow-[0_16px_36px_-20px_rgba(48,64,49,0.45)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#304031] hover:text-[#FEF6D6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#304031]"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                  />
                </svg>
                Log in to your account
              </button>
            ) : onboardingStarted ? (
              <button
                type="button"
                onClick={handleSendEmail}
                disabled={sending || sent}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#304031] bg-[#FEF6D6] px-6 py-4 text-lg font-semibold text-[#304031] shadow-[0_16px_36px_-20px_rgba(48,64,49,0.45)] transition duration-200 hover:-translate-y-0.5 hover:bg-[#304031] hover:text-[#FEF6D6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#304031] disabled:pointer-events-none disabled:opacity-50"
              >
                {sending ? (
                  <>
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#304031] border-t-transparent"></div>
                    Sending...
                  </>
                ) : sent ? (
                  <>
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    Email sent
                  </>
                ) : (
                  <>
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Send email to continue
                  </>
                )}
              </button>
            ) : null}

            {/* Back link */}
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate('/')}
                className="text-sm text-[#214D3C] hover:text-[#304031] underline underline-offset-4"
              >
                ← Back to start
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
